import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import debounce from "lodash.debounce";
import DashboardNavbar from "./DashboardNavbar";
import { useLoading } from "./LoadingContext";
import Pagination from "../Pagination";

const ResumeUpload = () => {
  const location = useLocation();
  const { taskId, matchedData } = location.state || {};
  const [data, setData] = useState(matchedData || []);
  const { loading, setLoading } = useLoading();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const debouncedSearch = debounce((searchQuery) => {
    setSearchQuery(searchQuery);
  }, 500);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  // Step 1: Filter the data first
  const filteredItems = data.filter(
    (item) =>
      (item.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      // (item.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      // (item.phone_number?.toString() || "").includes(searchQuery) ||
      // (item.experience?.toLowerCase() || "").includes(
      //   searchQuery.toLowerCase()
      // ) ||
      // (item.education?.toLowerCase() || "").includes(
      //   searchQuery.toLowerCase()
      // ) ||
      // (item.skills?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      // (item.reason?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      // (item.file_name?.toLowerCase() || "").includes(
      //   searchQuery.toLowerCase()
      // ) ||
      (item.validation?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  // Step 2: Calculate total pages based on filtered results
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Step 3: Ensure currentPage does not exceed total pages (fixes "No Data Available" issue)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1); // Reset to first page when search results change
    }
  }, [searchQuery, totalPages]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // Sorting logic
  const sortedItems = [...paginatedItems].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === "string") {
        return (
          aValue.localeCompare(bValue) *
          (sortConfig.direction === "ascending" ? 1 : -1)
        );
      } else if (typeof aValue === "number") {
        return (
          (aValue - bValue) * (sortConfig.direction === "ascending" ? 1 : -1)
        );
      }
    }
    return 0;
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "▲▼"; // Default arrow for unsorted columns
    return sortConfig.direction === "ascending" ? "▲" : "▼";
  };

  useEffect(() => {
    const fetchMatchedData = async () => {
      // if (!taskId) return;
      console.log("Task ID:", taskId);

      if (!taskId) {
        console.error("Task ID is missing!");
      }

      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/screener/upload-directory/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ task_id: taskId }),
          }
        );

        console.log("Task ID in state:", taskId);

        if (response.ok) {
          const result = await response.json();
          setData(result.data || []);
        } else {
          console.error("Failed to fetch matched data");
        }
      } catch (error) {
        console.error("An error occurred while fetching matched data", error);
      } finally {
        setLoading(false);
      }
    };

    if (!matchedData) {
      fetchMatchedData();
    }
  }, [taskId, matchedData]);

  const handleDownload = async () => {
    if (!taskId) {
      console.error("No task ID available for download");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/screener/download-csv/${taskId}`,
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Screenify_Candidate_Screening_Report.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Download failed");
      }
    } catch (error) {
      console.error("An error occurred during download", error);
    }
  };

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">&nbsp;Data is Fetching.... </h3>
      </div>
    );
  }

  return (
    <>
      <DashboardNavbar />
      <div className="container py-3 main-content">
        <h3 className="text-center font_header">Matched Results</h3>
        <div className="row align-items-center">
          <div className="col-sm-12 mt-2 mb-4 text-gred">
            <div className="d-flex justify-content-between align-items-start">
              <form className="form-inline d-flex align-items-start flex-column text-dark fw-bold">
                <label htmlFor="search" className="mb-1">
                  Search By Name or Validation:
                </label>
                <input
                  className="form-control border-dark text-dark"
                  type="search"
                  title="Search"
                  aria-label="Search"
                  onChange={handleSearch}
                  value={search}
                />
              </form>
              <button
                className="btn btn-outline-info ms-2 mt-4"
                title="Download Data"
                onClick={handleDownload}
                style={{ flexShrink: 0 }}
              >
                Download
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="table-responsive table-wrapper">
            <table
              className="table table-hover table-striped table-bordered"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "800px",
              }}
            >
              <thead className="text-center tb_header">
                <tr>
                  <th
                    style={{ width: "200px", cursor: "pointer" }}
                    onClick={() => requestSort("job_description_id")}
                  >
                    JD ID {getSortArrow("job_description_id")}
                  </th>
                  {/* <th
                    style={{ width: "200px", cursor: "pointer" }}
                    onClick={() => requestSort("name")}
                  >
                    Name {getSortArrow("name")}
                  </th> */}
                  <th
                    style={{ width: "200px", cursor: "pointer" }}
                    onClick={() => requestSort("phone_number")}
                  >
                    Phone Number {getSortArrow("phone_number")}
                  </th>
                  <th
                    style={{ width: "300px", cursor: "pointer" }}
                    onClick={() => requestSort("email")}
                  >
                    Email {getSortArrow("email")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("experience")}
                  >
                    Experience {getSortArrow("experience")}
                  </th>
                  <th
                    style={{ width: "250px", cursor: "pointer" }}
                    onClick={() => requestSort("experience")}
                  >
                    Years of Experience {getSortArrow("experience")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("education")}
                  >
                    Education {getSortArrow("education")}
                  </th>
                  <th
                    style={{ width: "200px", cursor: "pointer" }}
                    onClick={() => requestSort("name")}
                  >
                    Name {getSortArrow("name")}
                  </th>
                  <th
                    style={{ width: "350px", cursor: "pointer" }}
                    onClick={() => requestSort("skills")}
                  >
                    Skills {getSortArrow("skills")}
                  </th>
                  <th
                    style={{ width: "150px", cursor: "pointer" }}
                    onClick={() => requestSort("validation")}
                  >
                    Validation {getSortArrow("validation")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("reason")}
                  >
                    Reason {getSortArrow("reason")}
                  </th>
                  <th
                    style={{ width: "400px", cursor: "pointer" }}
                    onClick={() => requestSort("file_name")}
                  >
                    File Name {getSortArrow("file_name")}
                  </th>
                </tr>
              </thead>
              <tbody className="text-center table-secondary border-dark">
                {sortedItems.length > 0 ? (
                  sortedItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.job_description_id}</td>
                      {/* <td>{item.name}</td> */}
                      <td>{item.phone_number}</td>
                      <td>{item.email}</td>
                      <td>{item.experience}</td>
                      <td>{item.experience_years}</td>
                      <td>{item.education}</td>
                      <td>{item.name}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.skills}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.validation}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.reason}
                      </td>
                      {/* <td>{item.file_name}</td> */}
                      <td>
                        {item.file_name && item.file_url ? (
                          <a
                            href={item.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#007BFF",
                              textDecoration: "underline",
                            }}
                          >
                            {item.file_name}
                          </a>
                        ) : (
                          item.file_name || "-"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-start">
                      <h5>No Data Available</h5>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination Code */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginate={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
};

export default ResumeUpload;
